import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { NoteEntity, NoteSourceFilter, noteSchema } from '@/entities/Notes';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api/api';
import { schemeParseMultiple } from '@/utils/schemeParse';

import { ConfirmPopupStore } from '../ConfirmPopupStore';

export type NotesStoreParams = {
  organizationId: string;
};

export class NotesStore implements ILocalStore {
  private readonly _organizationId: string;
  private _sourceFilter: NoteSourceFilter = 'all';
  private _documentFilterId: string | null = null;
  private _lastRemovedId: string | null = null;
  readonly meta = new MetaStore();
  readonly popupMeta = new MetaStore();
  readonly collection = new CollectionStore<NoteEntity, NoteEntity>({
    parseItem: (data: NoteEntity) => data,
    getEntityKey: (data: NoteEntity) => String(data.id),
  });
  readonly removePopup: ConfirmPopupStore;

  constructor({ organizationId }: NotesStoreParams) {
    this._organizationId = organizationId;

    this.removePopup = new ConfirmPopupStore({
      onConfirm: (entityId) => this._onRemove(this._organizationId, entityId),
    });
    makeObservable<NotesStore, '_organizationId' | '_sourceFilter' | '_lastRemovedId'>(this, {
      _organizationId: observable,
      _sourceFilter: observable,
      _lastRemovedId: observable,
      sourceFilter: computed,
      filteredItems: computed,
      hasFilteredNotes: computed,
      isFilterActive: computed,
      lastRemovedId: computed,
      fetchNotes: action.bound,
      setSourceFilter: action.bound,
      clearLastRemoved: action.bound,
    });
  }

  get sourceFilter(): NoteSourceFilter {
    return this._sourceFilter;
  }

  get filteredItems(): NoteEntity[] {
    return this.collection.items;
  }

  get hasFilteredNotes(): boolean {
    return this.collection.items.length > 0;
  }

  get isFilterActive(): boolean {
    return this._sourceFilter !== 'all';
  }

  get lastRemovedId(): string | null {
    return this._lastRemovedId;
  }

  clearLastRemoved() {
    this._lastRemovedId = null;
  }

  setSourceFilter(value: NoteSourceFilter) {
    this._sourceFilter = value;
    this.fetchNotes();
  }

  async remove(entityId: string) {
    return this._onRemove(this._organizationId, entityId);
  }

  private async _onRemove(organizationId: string, entityId: string) {
    this.popupMeta.setLoadedStartMeta();

    const response = await call({
      endpoint: ENDPOINTS.note.delete(organizationId, entityId),
      method: 'DELETE',
    });

    runInAction(() => {
      if (response.isError) {
        notificationsStore.showError(response.error.message ?? 'Не удалось удалить заметку');
        this.popupMeta.setLoadedErrorMeta();

        return;
      }

      notificationsStore.showSuccess('Заметка удалена');
      this.popupMeta.setLoadedSuccessMeta();
      this._lastRemovedId = entityId;
      this.fetchNotes(this._documentFilterId);
    });
  }

  async fetchNotes(documentId?: string | null) {
    if (typeof documentId !== 'undefined') {
      this._documentFilterId = documentId ? String(documentId) : null;
    }

    this.collection.clear();

    this.meta.setLoadedStartMeta();

    const params = new URLSearchParams();

    if (this._documentFilterId) {
      params.append('artifact_type', 'document');
      params.append('artifact_id', this._documentFilterId);
    }

    if (this._sourceFilter !== 'all') {
      params.append('artifact_type', this._sourceFilter);
    }

    const endpoint = ENDPOINTS.note.list(this._organizationId);
    const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

    const response = await call<{ items: NoteEntity[] }>({
      endpoint: url,
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      const parsed = schemeParseMultiple(noteSchema, response.data.items);

      this.collection.pushRaws(parsed);

      this.meta.setLoadedSuccessMeta();
    });
  }

  destroy() {
    this.collection.destroy();
    this.meta.destroy();
    this.popupMeta.destroy();
    this.removePopup.destroy();
  }
}
